import { RFC3339timestamp } from "@bitory-gmbh/nidavellir";
import { backendApi } from "@/services/backend/api";
import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import { providesEntity } from "@/services/backend/_lib/tag-helpers";
import { Address } from "@/services/backend/addresses/types/address";
import {
  Building,
  BuildingList,
  BuildingOrderBy,
} from "@/services/backend/buildings/types/building";
import { LocationType } from "@/services/backend/buildings/types/location-type";
import { Filter } from "@/shared/lib/filter/filter";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";

function invalidatesBuilding(
  _result: unknown,
  _error: unknown,
  request: { id: string },
) {
  return [
    { type: "Building" as const, id: request.id },
    { type: "Building" as const, id: "LIST" },
    "BuildingByAddress" as const,
  ];
}

const buildingApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["Building", "BuildingByAddress"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      buildingsCreate: build.mutation<Building, CreateBuildingRequest>({
        query: (request) => ({
          url: `/buildings`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "Building", id: request.id },
          { type: "Building", id: "LIST" },
          "BuildingByAddress",
        ],
      }),
      buildingsUpdate: build.mutation<Building, UpdateBuildingRequest>({
        query: (request) => ({
          url: `/buildings`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "Building", id: request.id },
          { type: "Building", id: "LIST" },
          "BuildingByAddress",
        ],
      }),
      buildingsShow: build.query<Building, ShowBuildingRequest>({
        query: (request) => ({
          url: `/buildings`,
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("Building"),
      }),
      buildingsList: build.query<BuildingList, ListBuildingRequest>({
        query: (request) => ({
          url: `/buildings/list`,
          method: "GET",
          params: base64ListRequest(request),
        }),
        providesTags: [{ type: "Building", id: "LIST" }],
      }),
      buildingsLevelCreate: build.mutation<Building, CreateLevelRequest>({
        query: (request) => ({
          url: `/buildings/level`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: invalidatesBuilding,
      }),
      buildingsLevelUpdate: build.mutation<Building, UpdateLevelRequest>({
        query: (request) => ({
          url: `/buildings/level`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: invalidatesBuilding,
      }),
      buildingsLevelDelete: build.mutation<Building, DeleteLevelRequest>({
        query: (request) => ({
          url: `/buildings/level`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: invalidatesBuilding,
      }),
      buildingsAreaCreate: build.mutation<Building, CreateAreaRequest>({
        query: (request) => ({
          url: `/buildings/area`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: invalidatesBuilding,
      }),
      buildingsAreaUpdate: build.mutation<Building, UpdateAreaRequest>({
        query: (request) => ({
          url: `/buildings/area`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: invalidatesBuilding,
      }),
      buildingsAreaDelete: build.mutation<Building, DeleteAreaRequest>({
        query: (request) => ({
          url: `/buildings/area`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: invalidatesBuilding,
      }),
      buildingsDelete: build.mutation<Building, DeleteBuildingRequest>({
        query: (request) => ({
          url: `/buildings`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "Building", id: request.id },
          { type: "Building", id: "LIST" },
          "BuildingByAddress",
        ],
      }),
    }),
  });

export const {
  useBuildingsCreateMutation,
  useBuildingsUpdateMutation,
  useBuildingsShowQuery,
  useBuildingsListQuery,
  useBuildingsLevelCreateMutation,
  useBuildingsLevelUpdateMutation,
  useBuildingsLevelDeleteMutation,
  useBuildingsAreaCreateMutation,
  useBuildingsAreaUpdateMutation,
  useBuildingsAreaDeleteMutation,
  useBuildingsDeleteMutation,
} = buildingApi;

export interface CreateBuildingRequest {
  id: string;
  companyId: string;
  customerId: string;
  name: string;
  address: Address;
}

export interface UpdateBuildingRequest {
  id: string;
  name: string;
  address: Address;
}

export interface ShowBuildingRequest {
  id: string;
}

export interface ListBuildingRequest {
  company?: Filter<string>;
  customer?: Filter<string>;
  search?: Filter<string>;
  name?: Filter<string>;
  address?: Filter<string>;
  createdAt?: Filter<RFC3339timestamp>;
  updatedAt?: Filter<RFC3339timestamp>;
  orderBy?: OrdrBy<BuildingOrderBy>[];
  limit?: number;
  offset?: number;
}

export interface CreateLevelRequest {
  id: string;
  levelId: string;
  name: string;
  type: LocationType;
}

export interface UpdateLevelRequest {
  id: string;
  levelId: string;
  name: string;
  type: LocationType;
}

export interface DeleteLevelRequest {
  id: string;
  levelId: string;
}

export interface CreateAreaRequest {
  id: string;
  areaId: string;
  levelId?: string;
  name: string;
  type: LocationType;
}

export interface UpdateAreaRequest {
  id: string;
  areaId: string;
  levelId?: string;
  name: string;
  type: LocationType;
}

export interface DeleteAreaRequest {
  id: string;
  areaId: string;
}

export interface DeleteBuildingRequest {
  id: string;
}
